import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
    AdminSetAttachedUserDto,
    MembershipSubscriptionDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { GET_USER_MEMBERSHIPS_QUERY_KEY } from './useGetUserMemberships';
import { GET_USER_SUBSCRIPTIONS_QUERY_KEY } from './useGetUserSubscriptions';

/**
 * MYS-117 Duo — operator attach/replace of a subscription's beneficiary.
 * Admin route (`/attached-user/admin`): AdminAuthGuard + org access, and it
 * alone may carry `overrideThrottle` (once-per-billing-period change limit).
 *
 * Invalidates the org roster and BOTH players' membership lists — attach
 * mints/deactivates real UserMembership rows.
 */
export const useSetAttachedUser = (organizationId?: string) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['setAttachedUser'],
        mutationFn: async ({
            subscriptionId,
            body,
        }: {
            subscriptionId: string;
            body: AdminSetAttachedUserDto;
        }) => {
            return apiClient.fetchApi<
                MembershipSubscriptionDto,
                AdminSetAttachedUserDto
            >({
                method: 'PUT',
                endpoint: `membership-subscriptions/${subscriptionId}/attached-user/admin`,
                body,
            });
        },
        onSuccess: (data) => {
            if (organizationId) {
                queryClient.invalidateQueries({
                    queryKey: [organizationId, 'users-with-memberships'],
                });
            }
            queryClient.invalidateQueries({
                queryKey: GET_USER_MEMBERSHIPS_QUERY_KEY(data.userId),
            });
            // DASHBOARD-17: DuoAttachmentCard now reads the per-user
            // subscriptions key, not the org roster invalidated above — without
            // this the card keeps rendering the previous « Rattaché : … » after
            // a successful attach.
            queryClient.invalidateQueries({
                queryKey: GET_USER_SUBSCRIPTIONS_QUERY_KEY(data.userId),
            });
            if (data.attachedUser?.id) {
                queryClient.invalidateQueries({
                    queryKey: GET_USER_MEMBERSHIPS_QUERY_KEY(data.attachedUser.id),
                });
                queryClient.invalidateQueries({
                    queryKey: GET_USER_SUBSCRIPTIONS_QUERY_KEY(data.attachedUser.id),
                });
            }
        },
    });
};

export const useRemoveAttachedUser = (organizationId?: string) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['removeAttachedUser'],
        mutationFn: async ({ subscriptionId }: { subscriptionId: string }) => {
            return apiClient.fetchApi<MembershipSubscriptionDto, {}>({
                method: 'DELETE',
                endpoint: `membership-subscriptions/${subscriptionId}/attached-user/admin`,
            });
        },
        onSuccess: (data) => {
            if (organizationId) {
                queryClient.invalidateQueries({
                    queryKey: [organizationId, 'users-with-memberships'],
                });
            }
            queryClient.invalidateQueries({
                queryKey: GET_USER_MEMBERSHIPS_QUERY_KEY(data.userId),
            });
            queryClient.invalidateQueries({
                queryKey: GET_USER_SUBSCRIPTIONS_QUERY_KEY(data.userId),
            });
            // The detached beneficiary's id is no longer on the DTO — sweep
            // every cached membership list so their fiche can't show a stale
            // active Duo row. DASHBOARD-17 adds 'subscriptions' to the sweep:
            // the beneficiary's own DuoAttachmentCard reads that key, and it is
            // the surface that renders the row being detached.
            queryClient.invalidateQueries({
                predicate: (query) =>
                    query.queryKey.includes('memberships') ||
                    query.queryKey.includes('subscriptions'),
            });
        },
    });
};
